import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { useApiPostRequest } from '@/api/rest/hooks/useApiPostRequest';
import {
  GET_NDI_CREDITS_BALANCE_PATH,
  GET_NDI_CREDITS_TRANSACTIONS_PATH,
  GetNDICreditsBalanceErrorDto,
  GetNDICreditsTransactionsErrorDto,
  GetNDICreditsTransactionsRequestQueryParams,
  GetNDICreditsTransactionsSuccessDto,
  NDICreditsBalance,
  POST_NDI_CREDITS_ADD_PATH,
  POST_NDI_MANUAL_JOB_SUBSCRIPTION_COST_PATH,
  PostNDICreditsAddErrorDto,
  PostNDICreditsAddRequestBodyData,
  PostNDICreditsAddRequestQueryParams,
  PostNDIManualJobSubscriptionCostErrorDto,
  PostNDIManualJobSubscriptionCostRequestBodyData,
} from '@/api/rest/resources/types/ndiCredits';

export const useNdiCreditsBalance = (organizationId: string) => {
  const queryFn = async () => {
    return getRequest<NDICreditsBalance, undefined, { org: string }>(GET_NDI_CREDITS_BALANCE_PATH, undefined, {
      org: organizationId,
    });
  };

  return useSuspenseQuery<NDICreditsBalance, GetNDICreditsBalanceErrorDto>({
    queryKey: ['ndi-credits-balance', organizationId],
    queryFn,
    staleTime: 1000 * 60,
  });
};

export const useNdiCreditsTransactions = (
  organizationId: string,
  queryParams?: Omit<GetNDICreditsTransactionsRequestQueryParams, 'org'>,
) => {
  const queryFn = async () => {
    return getRequest<GetNDICreditsTransactionsSuccessDto, undefined, GetNDICreditsTransactionsRequestQueryParams>(
      GET_NDI_CREDITS_TRANSACTIONS_PATH,
      undefined,
      { org: organizationId, ...queryParams },
    );
  };

  return useSuspenseQuery<GetNDICreditsTransactionsSuccessDto, GetNDICreditsTransactionsErrorDto>({
    queryKey: ['ndi-credits-transactions', organizationId, queryParams],
    queryFn,
    staleTime: 1000 * 60,
  });
};

export const usePostNdiCreditsAdjustment = () => {
  return useApiPostRequest<
    undefined,
    PostNDICreditsAddRequestQueryParams,
    PostNDICreditsAddRequestBodyData,
    NDICreditsBalance,
    PostNDICreditsAddErrorDto
  >(POST_NDI_CREDITS_ADD_PATH);
};

export const usePostNdiManualJobSubscriptionCost = () => {
  return useApiPostRequest<
    undefined,
    undefined,
    PostNDIManualJobSubscriptionCostRequestBodyData,
    NDICreditsBalance,
    PostNDIManualJobSubscriptionCostErrorDto
  >(POST_NDI_MANUAL_JOB_SUBSCRIPTION_COST_PATH);
};
